// src/components/common/Tooltip.tsx
import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';

interface TooltipProps {
  text: string;
  targetRef: React.RefObject<HTMLElement>;
}

export const Tooltip: React.FC<TooltipProps> = ({ text, targetRef }) => {
  const [position, setPosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const handleMouseEnter = () => setVisible(true);
    const handleMouseLeave = () => setVisible(false);

    const target = targetRef.current;
    if (target) {
      target.addEventListener('mouseenter', handleMouseEnter);
      target.addEventListener('mouseleave', handleMouseLeave);
    }

    return () => {
      if (target) {
        target.removeEventListener('mouseenter', handleMouseEnter);
        target.removeEventListener('mouseleave', handleMouseLeave);
      }
    };
  }, [targetRef]);

  useEffect(() => {
    const updatePosition = () => {
      if (visible && targetRef.current) {
        const rect = targetRef.current.getBoundingClientRect();
        setPosition({
          top: rect.top + rect.height / 2 + window.scrollY, // Include scroll position
          left: rect.right + 8 + window.scrollX, // 8px offset to the right
        });
      }
    };

    updatePosition();

    // Update position on window resize or scroll
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition);

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition);
    };
  }, [visible, targetRef]);

  if (!visible) return null;

  return ReactDOM.createPortal(
    <span
      className="absolute px-2 py-1 bg-black text-white text-xs rounded z-50 whitespace-nowrap max-w-xs"
      style={{ top: position.top, left: position.left, transform: 'translateY(-50%)' }}
    >
      {text}
    </span>,
    document.body
  );
};
